"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Server, FileText, Globe, Check, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface Pillar {
  id: string;
  title: string;
  subtitle: string;
  summary: React.ReactNode;
  icon: React.ComponentType<{
    className?: string;
  }>;
  checklist: string[];
  honestNote: string;
}
const pillars: Pillar[] = [{
  id: "technical-access",
  title: "Technical access for AI crawlers",
  subtitle: "Pillar 01 • Crawl & Rendering Architecture",
  icon: Server,
  summary: <>
        AI answer engines must be able to discover, crawl, render, and interpret your public pages before they can evaluate your brand. This starts with clean{" "}
        <Link to="/TechnicalSeoPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
          technical SEO foundations
        </Link>
        .
      </>,
  checklist: ["Crawlability and indexability, complete sitemaps, clean URLs, and no accidental noindex directives", "Thoughtful robots.txt configuration so legitimate AI crawlers can reach public pages", "Self-referential and clean canonical tags to prevent ambiguous indexing", "Server-side or pre-rendered essential content where heavy JavaScript frameworks hide text", "Semantic HTML tags and a logical heading structure for natural reading flows", "Structured data for organisation, services, FAQs, articles, and local business entities", "Fast mobile performance and accessible responsive layouts"],
  honestNote: "Structured data clarifies meaning and relationships for models, but does not guarantee AI citation or inclusion."
}, {
  id: "content-optimised",
  title: "Content optimised for AI discovery",
  subtitle: "Pillar 02 • Answer-Ready Information",
  icon: FileText,
  summary: "Generative models summarise the web by identifying concise, well-evidenced answers to real search queries. Writing with clarity and structure enables systems to understand your core expertise directly.",
  checklist: ["Answer-focused pages that address buyer questions directly near the top of the content", "Clear, specific explanations rather than vague marketing rhetoric or generic claims", "Direct coverage of real buyer-intent questions, including comparisons, pricing approaches, and considerations", "Original expertise drawn from real business experience and proven working processes", "Accurate, dated, and referenced information that machines and humans can verify", "Useful, targeted FAQ blocks addressing specific buyer hesitations", "Balanced, honest comparisons that respect user search intent"],
  honestNote: "Mass-produced low-quality AI articles are counterproductive; every page needs editorial review. Most gains for SMEs come from structure and clarity, not volume."
}, {
  id: "entity-credibility",
  title: "Brand entity and credibility",
  subtitle: "Pillar 03 • External Verification",
  icon: Globe,
  summary: "Large language models do not evaluate pages in total isolation. They cross-reference names, services, and third-party context to understand whether an organisation is an established, trustworthy source.",
  checklist: ["Consistent business name, address, phone, and service descriptions across the site, Google Business Profile, and directories", "Clear organisation and About page content explicitly detailing who I serve and what I do", "Credible third-party mentions, industry references, and relevant editorial links across the web", "Consistent author and consultant profiles establishing verified subject-matter background"],
  honestNote: "These signals help systems describe a brand accurately and aid discoverability, but they are not guaranteed ranking factors — authority is one factor among many."
}];
export default function GeoStrategy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="geostrategyfoundations" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <Badge variant="secondary" className="px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase mb-4 inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-accent" />
            <span>Core GEO Methodology</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground mb-5">
            Generative Engine Optimisation Strategy
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            GEO is making a business a clear, credible source when AI systems summarise the web. It amplifies existing SEO foundations and cannot compensate for weak products, poor reviews or thin content. Because some GEO tactics are still experimental, I treat them as hypotheses to test, not rules.
          </p>
        </div>

        {/* Three Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {pillars.map((pillar, index) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.id} data-index={index} variants={cardVariants} className="flex flex-col h-full">
                <Card className="flex flex-col justify-between h-full rounded-xl border border-border shadow-sm hover:border-primary/40 bg-card transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-primary">
                        <Icon className="size-6" />
                      </div>
                      <span className="text-xs font-mono font-medium text-muted-foreground">
                        0{index + 1}
                      </span>
                    </div>

                    <p className="text-xs font-semibold uppercase tracking-wider text-accent font-sans">
                      {pillar.subtitle}
                    </p>

                    <CardTitle className="text-xl md:text-2xl font-serif font-semibold text-card-foreground mt-1">
                      {pillar.title}
                    </CardTitle>

                    <div className="text-sm text-muted-foreground mt-3 leading-relaxed">
                      {pillar.summary}
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 py-2 flex flex-col justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 font-sans">
                        Practical checklist:
                      </div>
                      <ul className="space-y-2.5 mb-6">
                        {pillar.checklist.map((item, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                            <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                              <Check className="size-2.5 stroke-[3]" />
                            </span>
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="p-3.5 rounded-lg bg-muted/60 border border-border/80 text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground font-medium">Please note: </strong>
                      {pillar.honestNote}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}